import * as React from 'react';
import styled from 'styled-components';
import Values from 'values.js';

import { useTheme } from 'src/providers/ThemeProvider';

import { ClydeButton } from './ClydeButton';

import { ILinkButtonColors } from 'src/types/IButton';

// NOTE: This wrapper is for buttons that look like links, e.g. the "What's Covered?" button

const StyledLinkButton = styled(ClydeButton)<{ colors: ILinkButtonColors }>`
  ${({ colors }) => (`
    color: ${colors.text};

    &:focus, &:hover {
      color: ${colors.textHover};
    }

    &:active {
      color: ${colors.textPressed};
    }
  `)}
`;

type AnchorProps = React.ComponentPropsWithoutRef<'a'>;
type ButtonProps = React.ComponentPropsWithoutRef<'button'>;
type LinkButtonProps = AnchorProps | ButtonProps;

export const LinkButton = React.forwardRef<HTMLAnchorElement | HTMLButtonElement, LinkButtonProps>((props, ref) => {
  const { merchantColor } = useTheme();

  const color = React.useMemo(() => new Values(merchantColor), [merchantColor]);
  const colors = {
    // NOTE: this logic doesn't work for light brand colors (closer to white than to black)
    text: color.hexString(),
    textHover: color.shade(15).hexString(),
    textPressed: color.shade(30).hexString(),
  };

  if ('href' in props) {
    // Renders an anchor tag
    return (
      <StyledLinkButton
        as="a"
        colors={colors}
        ref={ref as React.RefObject<HTMLAnchorElement>}
        {...props as AnchorProps}
      />
    );
  }

  if ('type' in props) {
    // Renders a button that looks like a link
    return (
      <StyledLinkButton
        colors={colors}
        ref={ref as React.RefObject<HTMLButtonElement>}
        {...props as ButtonProps}
      />
    );
  }

  return null;
});

LinkButton.displayName = 'LinkButton';
